import React, { useMemo, useState } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { Box, Flex, Spinner, Text } from '@chilipiper/design-system'
import { formatInTimeZone, getClientTimezone } from '@chilipiper/date-time'
import { useFindPersonalDetailsByIds } from '@chilipiper/api-queries/src/personal-details'
import { LinkButton, Icon } from '@chilipiper/design-system/src/new'
import {
  FlexibleRoundRobinMeetingStartTime,
  FlexibleRoundRobinMeetingStartTimes,
  UsersCombinedMeetingStartTimes,
} from '@chilipiper/api-client/src/user-availability'
import * as v from 'valibot'
import { MeetingInvite } from '@chilipiper/api-client/src/meeting-types'
import { DataFieldDataType } from '@chilipiper/api-client/src/chili-crm'
import { getUserNameOrEmail } from '@chilipiper/admin-components'
import { IconError } from '@chilipiper/icons/figma-generated/src/icon'
import { buildReserveTimeSlotPayload } from '@chilipiper/booking-flow/src/new/helpers/availability'
import { useTranslations } from '../../../context/TranslationContext'
import { useMessageFeedContext } from '../../../chat-widget/MessageFeedContext'
import {
  useMeeting,
  useMeetingQueryOptions,
  useReserveTimeSlot,
  useUpdateMeeting,
} from '../../../api-queries/chat-platform-chat'
import { BookMeetingBlock as BookMeetingBlockType } from '../../../chat-data/types'
import { useExpiredSession } from '../../ExpiredSessionContext'
import { StyledBookMeetingContainer } from './styles'
import { BookingMeeting } from './BookingMeeting'
import { ScheduleHeader } from './components/ScheduleHeader'
import { BookingConfirmation } from './BookingConfirmation'
import { Timeout } from './Timeout'
import { ErrorSchema, GuestFormValues } from './helpers'

interface Props {
  isRescheduling: boolean
  onReschedulingChange: (isRescheduling: boolean) => void
  isFlexibleReassignment: boolean
  block: Omit<BookMeetingBlockType, 'assignees'> & Partial<Pick<BookMeetingBlockType, 'assignees'>>
  fields: Array<{
    dataType?: DataFieldDataType
    description?: string
    error?: string
    id: string
    initialValue?: string
    hiddenValue?: string
    isRequired: boolean
    label?: string
  }>
  isBookingMeeting: boolean
  isNoAvailableAssignee?: boolean
  meetingInvite?: MeetingInvite
  isMeetingReserved: boolean
  claimMeetingAttemptReservationError: unknown
  refetchAvailability: () => void
  isLoading: boolean
  timeSlots?: UsersCombinedMeetingStartTimes | FlexibleRoundRobinMeetingStartTimes
  onBookMeeting: (data: {
    guestFormData: GuestFormValues
    onError: () => void
    selectedTime: string
    timezone: string
  }) => void
  guestEmail?: string
  assigneesIds?: string[]
  error?: string
  suggestedTimes?: Array<string>
  initialTimezone?: string
}

export const BookingFlowBlock = ({
  block,
  suggestedTimes,
  onBookMeeting,
  isBookingMeeting,
  isMeetingReserved,
  guestEmail,
  refetchAvailability,
  timeSlots,
  isLoading,
  assigneesIds,
  isNoAvailableAssignee,
  meetingInvite,
  claimMeetingAttemptReservationError,
  fields,
  error,
  isRescheduling,
  onReschedulingChange,
  isFlexibleReassignment,
  initialTimezone,
}: Props) => {
  const isExpiredSession = useExpiredSession()
  const meetingId = block.meetingId
  const queryClient = useQueryClient()
  const { tenantId = '', orgDetails } = useMessageFeedContext()
  const { data: meeting } = useMeeting(tenantId, meetingId)
  const meetingUserAttendees = meeting?.attendees
    ?.filter(attendee => attendee.type === 'User')
    .map(attendee => attendee.value)
  const providedAssignees = assigneesIds ?? block.assignees
  const assignees = useAssignees(meetingUserAttendees ?? providedAssignees ?? [])
  const { startTimes, ignoreSuggestedTimes, toggleIgnoreSuggestedTimes } = useStartTimes(
    timeSlots,
    suggestedTimes
  )

  const {
    mutateAsync: reserveTimeSlot,
    isPending: isReservingTimeSlot,
    error: reservationError,
  } = useReserveTimeSlot(tenantId)
  const {
    mutate: updateMeeting,
    isPending: isUpdatingMeeting,
    error: updateMeetingError,
  } = useUpdateMeeting(tenantId, meetingId ?? '')
  const meetingQueryOptions = useMeetingQueryOptions()
  const [bookingLoader, showFullCalendar, showOnlySuggestedTimes] = useTranslations([
    'bookingLoader',
    'showFullCalendar',
    'showOnlySuggestedTimes',
  ])

  const [selectedTime, setSelectedTime] = useState<string>()
  const [timezone, setTimezone] = useState(initialTimezone ?? getClientTimezone())

  const isWaitingForMeetingId = isMeetingReserved && !meetingId
  const isBooking =
    isReservingTimeSlot || isUpdatingMeeting || isBookingMeeting || isWaitingForMeetingId

  const handleBookMeeting = ({
    guestFormData,
    selectedTime,
  }: {
    guestFormData: GuestFormValues
    selectedTime: string
  }) => {
    return onBookMeeting({
      selectedTime,
      onError: refetchAvailability,
      timezone,
      guestFormData,
    })
  }
  const handleRescheduleMeeting = async (guestEmail?: string) => {
    if (!selectedTime || !meetingInvite?.duration || !guestEmail) {
      return
    }
    try {
      let id: string | undefined
      if (!isFlexibleReassignment) {
        const data = await reserveTimeSlot(
          buildReserveTimeSlotPayload({
            meetingTypeRef: { id: block.meetingTypeId, timestamp: new Date().toISOString() },
            slotStartsAt: selectedTime,
            userIds: assignees.map(assignee => assignee.id),
            meetingTypeOverride: {
              duration: meetingInvite?.duration,
            },
          })
        )
        id = data.id
      }
      updateMeeting(
        {
          attendees: { type: 'LeaveAsIs' },
          location: { type: 'LeaveAsIs' },
          noShowStatus: { type: 'LeaveAsIs' },
          notificationBehaviour: 'Send',
          meetingTypeOverride: {
            type: 'Replace',
            value: {
              meetingInvite: {
                description: {
                  type: meetingInvite.description ? 'Replace' : 'LeaveAsIs',
                  value: meetingInvite.description,
                },
                title: {
                  type: meetingInvite.title ? 'Replace' : 'LeaveAsIs',
                  value: meetingInvite.title,
                },
              },
              buffers: {
                before: { type: 'LeaveAsIs' },
                after: { type: 'LeaveAsIs' },
              },
              reminder: {
                reminders: {
                  type: 'LeaveAsIs',
                },
              },
              advancedSettings: {
                externalGuestsInCalendarEvent: {
                  type: 'LeaveAsIs',
                },
              },
            },
          },
          timeAndAssignment: {
            type: 'Replace',
            value: id
              ? {
                  assignment: { type: 'LeaveAsIs' },
                  hostId: { type: 'LeaveAsIs' },
                  type: 'WithReservationId',
                  reservationId: id,
                }
              : {
                  type: 'WithMultipleAssigneesDistribution',
                  availableAssignees:
                    getTimeSlotAssignees(timeSlots, selectedTime) ??
                    assignees.map(assignee => assignee.id),
                  interval: {
                    startsAt: selectedTime,
                    duration: meetingInvite?.duration,
                  },
                  meetingTypeReference: { type: 'LeaveAsIs' },
                },
          },
          origin: { type: 'RescheduleLink' },
        },
        {
          onSettled: () => {
            refetchAvailability()
          },
          onSuccess: () => {
            queryClient.cancelQueries({
              queryKey: meetingQueryOptions(tenantId, meetingId ?? '').queryKey,
            })
            queryClient.setQueryData(
              meetingQueryOptions(tenantId, meetingId ?? '').queryKey,
              previousData => {
                if (previousData) {
                  return {
                    ...previousData,
                    dateTime: {
                      ...previousData.dateTime,
                      start: selectedTime,
                      end: new Date(
                        new Date(selectedTime).valueOf() + getMilliseconds(meetingInvite?.duration)
                      ).toISOString(),
                    },
                  }
                }
              }
            )
            onReschedulingChange(false)
          },
        }
      )
    } catch (error) {
      console.error(error)
    }
  }

  const host = assignees.find(assignee => assignee.id === meeting?.hostId)
  const state = useMemo(() => {
    if (isRescheduling) {
      return 'rescheduling'
    }
    if (meetingId !== undefined) {
      return 'scheduled'
    }
    return 'scheduling'
  }, [isRescheduling, meetingId])

  return (
    <Box w='100%' data-test-id='WidgetBookMeetingBlock' inert={isExpiredSession}>
      <StyledBookMeetingContainer w='100%' flexDirection='column' borderRadius={8}>
        {(() => {
          if (error) {
            return (
              <Flex p={7} my={1} justifyContent='center' alignItems='center' gap={2}>
                <Icon icon={IconError} color='fg/caution' />
                <Text color='fg/caution' textStyle='body-monoline'>
                  {error}
                </Text>
              </Flex>
            )
          }

          if (isBooking) {
            return (
              <Flex p={7} my={1} justifyContent='center' alignItems='center' gap={2}>
                <Spinner />
                <Text color='fg/moderate' textStyle='body-monoline'>
                  {bookingLoader ?? 'Booking meeting'}
                </Text>
              </Flex>
            )
          }

          return (
            <>
              {state === 'scheduling' && !isLoading && <Timeout timeout={block.timeout} />}
              <ScheduleHeader
                state={state}
                assignee={
                  host
                    ? host
                    : {
                        id: tenantId,
                        image: orgDetails?.logo,
                        name: orgDetails?.name ?? '',
                      }
                }
                displayName={(() => {
                  if (host?.name) {
                    return host.name
                  }
                  return orgDetails?.name
                })()}
              />
              {meetingId === undefined || isRescheduling ? (
                <BookingMeeting
                  suggestedTimesToggle={
                    suggestedTimes ? (
                      <LinkButton variant='minor' onClick={toggleIgnoreSuggestedTimes}>
                        {ignoreSuggestedTimes
                          ? (showOnlySuggestedTimes ?? 'Show only suggested times')
                          : (showFullCalendar ?? 'Show full calendar')}
                      </LinkButton>
                    ) : undefined
                  }
                  fields={
                    isRescheduling
                      ? []
                      : fields.filter(field => {
                          return field.id !== 'PersonEmail' || !guestEmail
                        })
                  }
                  isLoading={isLoading}
                  isNoAvailableAssignee={isNoAvailableAssignee}
                  isSlotTaken={
                    isSlotTaken(reservationError) ||
                    isSlotTaken(claimMeetingAttemptReservationError) ||
                    isSlotTaken(updateMeetingError)
                  }
                  cancelRescheduling={() => onReschedulingChange(false)}
                  startTimes={startTimes}
                  meetingTypeId={block.meetingTypeId}
                  locations={meetingInvite?.locations}
                  bookedTime={meeting?.dateTime}
                  guestEmail={guestEmail}
                  timezone={timezone}
                  onTimezoneChange={setTimezone}
                  onSchedule={guestFormData => {
                    if (isRescheduling) {
                      return handleRescheduleMeeting(guestEmail)
                    }
                    if (selectedTime) {
                      handleBookMeeting({ guestFormData, selectedTime })
                    }
                  }}
                  selectedTime={selectedTime}
                  onSelectedTimeChange={time => setSelectedTime(time)}
                  availableDays={
                    startTimes?.reduce(
                      (acc, startTime) => {
                        const day = formatInTimeZone(startTime, timezone, 'yyyy-MM-dd')
                        if (!acc[day]) {
                          acc[day] = true
                        }
                        return acc
                      },
                      {} as Record<string, boolean>
                    ) ?? {}
                  }
                  durationInMinutes={getMilliseconds(meetingInvite?.duration) / 60 / 1000}
                />
              ) : (
                <BookingConfirmation
                  meeting={meeting}
                  timezone={timezone}
                  onClick={() => onReschedulingChange(true)}
                  email={block.email}
                />
              )}
            </>
          )
        })()}
      </StyledBookMeetingContainer>
    </Box>
  )
}

/**
 * Parse time with milliseconds unit to number
 *
 * @example
 *   '1000 milliseconds' => 1000
 *   undefined => 0
 */
const getMilliseconds = (time?: string) => {
  const [milliseconds] = time?.split(' ') ?? ['0']
  return Number(milliseconds)
}

const isSlotTaken = (reservationError: unknown) => {
  const { success, output } = v.safeParse(ErrorSchema, reservationError)
  return Boolean(
    success &&
      (output.error.errorCode === 'user-availability-service.slot-is-taken' ||
        output.error.errorCode === 'user-availability-service.slot-is-unavailable')
  )
}

const useAssignees = (userIds: string[]) => {
  const { tenantId = '' } = useMessageFeedContext()
  const { data: assignees } = useFindPersonalDetailsByIds(tenantId, userIds)

  return Object.entries(assignees ?? {})?.map(([assigneeId, assignee]) => ({
    id: assigneeId,
    name: getUserNameOrEmail(assignee),
    image: assignee?.imageUrl,
  }))
}

const useStartTimes = (
  timeSlots?: { startTimes?: Array<FlexibleRoundRobinMeetingStartTime | string> },
  suggestedTimes?: Array<string>
) => {
  const [ignoreSuggestedTimes, setIgnoreSuggestedTimes] = useState(false)
  const availabilityStartTimes = useMemo(
    () =>
      timeSlots?.startTimes?.map(startTime => {
        const flexibleRoundRobinMeetingStartTime = (startTime as FlexibleRoundRobinMeetingStartTime)
          ?.startTime
        if (flexibleRoundRobinMeetingStartTime) {
          return flexibleRoundRobinMeetingStartTime
        }
        return startTime as string
      }),
    [timeSlots?.startTimes]
  )

  return {
    startTimes: ignoreSuggestedTimes
      ? availabilityStartTimes
      : (suggestedTimes ?? availabilityStartTimes),
    ignoreSuggestedTimes,
    toggleIgnoreSuggestedTimes: () => setIgnoreSuggestedTimes(prevState => !prevState),
  }
}

const getTimeSlotAssignees = (
  timeSlots?: UsersCombinedMeetingStartTimes | FlexibleRoundRobinMeetingStartTimes,
  selectedTime?: string
) => {
  if (!timeSlots) {
    return
  }
  if ('userIds' in timeSlots) {
    return timeSlots.userIds
  }
  const matchingTimeSlot = timeSlots.startTimes?.find(
    timeSlot => timeSlot.startTime === selectedTime
  )
  if (matchingTimeSlot) {
    return matchingTimeSlot.availableAssignees
  }
}
